import { apiFetch, parseJsonSafe } from './api';

export const IVA_RATE = 0.21;

// Fecha de hoy en formato YYYY-MM-DD en hora LOCAL (toISOString devuelve UTC:
// de madrugada en Madrid daría el día anterior).
export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Mapa canónico de colores Bootstrap por estado de factura (compartido por
// listado, detalle y cualquier otra vista de facturación).
export const statusBadge = (estado) => {
  const map = {
    Pagada: 'bg-success',
    Pendiente: 'bg-warning text-dark',
    Parcial: 'bg-warning text-dark',
    'Pagada parcial': 'bg-warning text-dark',
    Validada: 'bg-info text-dark',
    Anulada: 'bg-danger',
    Devuelta: 'bg-danger',
    Vencida: 'bg-danger',
    Borrador: 'bg-secondary',
    Proforma: 'bg-primary',
    Emitida: 'bg-info text-dark',
    Facturada: 'bg-success'
  };
  return map[estado] || 'bg-secondary';
};

export const monthOptions = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

export const getDayFromDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.getDate();
};

export const getMonthFromDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.getMonth() + 1;
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(num);
};

export const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-ES');
};

// Periodicidad legible de una factura recurrente a partir de recurrent_unit + recurrent_interval.
export const formatRecurrence = (unit, interval) => {
  const n = Number(interval) || 1;
  const u = String(unit || '').toLowerCase();
  if (u.startsWith('year') || u.startsWith('an')) return n === 1 ? 'Anual' : `Cada ${n} años`;
  if (u.startsWith('month') || u.startsWith('mes')) return n === 1 ? 'Mensual' : `Cada ${n} meses`;
  if (u.startsWith('week') || u.startsWith('seman')) return n === 1 ? 'Semanal' : `Cada ${n} semanas`;
  if (u.startsWith('day') || u.startsWith('di')) return n === 1 ? 'Diario' : `Cada ${n} días`;
  return u ? `${n} ${u}` : '-';
};

export const downloadFile = async (endpoint, filename, token, onUnauthorized) => {
  const res = await apiFetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 401) { if (onUnauthorized) onUnauthorized(); return; }
  if (!res.ok) {
    const data = await parseJsonSafe(res);
    throw new Error((data && data.message) || 'Error descargando archivo');
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'factura.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Abre el PDF en una pestaña nueva para VISUALIZARLO (sin descargar). Se abre la pestaña
// de forma síncrona dentro del gesto de click (evita el bloqueador de popups) y luego se
// carga el blob descargado con autenticación.
export const previewFile = async (endpoint, token, onUnauthorized) => {
  const win = window.open('', '_blank');
  try {
    const res = await apiFetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { if (win) win.close(); if (onUnauthorized) onUnauthorized(); return; }
    if (!res.ok) {
      if (win) win.close();
      const data = await parseJsonSafe(res);
      throw new Error((data && data.message) || 'No se pudo abrir la vista previa');
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    if (win) win.location.href = url; else window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
  } catch (err) {
    if (win) { try { win.close(); } catch (_e) { /* noop */ } }
    throw err;
  }
};

export const calculateInvoiceTotals = (lines = [], rate = IVA_RATE) => {
  const subtotalValue = lines.reduce((sum, ln) => {
    const lineTotal = Number(ln.total ?? (Number(ln.cantidad || 0) * Number(ln.precio || 0)));
    return sum + (Number.isNaN(lineTotal) ? 0 : lineTotal);
  }, 0);
  const ivaValue = subtotalValue * rate;
  const totalValue = subtotalValue + ivaValue;
  return { subtotalValue, ivaValue, totalValue };
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Importe NETO de una línea (bruto - descuento). El bruto es total (cantidad*precio).
export const lineNet = (ln = {}) => {
  const gross = Number(ln.total ?? (Number(ln.cantidad || 0) * Number(ln.precio || 0)));
  const desc = Number(ln.descuento || 0);
  const safeGross = Number.isNaN(gross) ? 0 : gross;
  return round2(safeGross * (1 - (Number.isNaN(desc) ? 0 : desc) / 100));
};

// Totales de un documento con IVA y descuento por línea (soporta 21/10/4/0% mezclados).
// Devuelve también el desglose de IVA por tipo para mostrar en pantalla y PDF.
export const calculateBudgetTotals = (lines = []) => {
  let subtotalValue = 0;
  let ivaValue = 0;
  const byRate = {};
  lines.forEach((ln) => {
    const net = lineNet(ln);
    const rate = ln.tva_tx != null && ln.tva_tx !== '' ? Number(ln.tva_tx) : 21;
    const safeRate = Number.isFinite(rate) ? rate : 21;
    const lineIva = round2(net * safeRate / 100);
    subtotalValue += net;
    ivaValue += lineIva;
    if (!byRate[safeRate]) byRate[safeRate] = { base: 0, iva: 0 };
    byRate[safeRate].base += net;
    byRate[safeRate].iva += lineIva;
  });
  subtotalValue = round2(subtotalValue);
  ivaValue = round2(ivaValue);
  const totalValue = round2(subtotalValue + ivaValue);
  return { subtotalValue, ivaValue, totalValue, byRate };
};
